// @flow strict

import * as React from 'react';

import {useSelector} from 'react-redux';
import moment from 'moment-timezone';
import {getTimezone} from 'src/selectors/date-time';

import useLabelTranslation from 'src/hooks/useLabelTranslation';

import ChatSoundEffect from 'src/components/messaging/sound-effect.jsx';
import {
  Toast,
  ToastBody,
  ToastFooter,
  ToastTitle,
} from '@spaced-out/ui-design-system/lib/components/Toast';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';

import bellMp3 from 'src/sounds/bell_ding_muted_lo.mp3';
import bellWav from 'src/sounds/bell_ding_muted_lo.wav';

import css from './lat-notification.css';


type LATNotificationProps = {
  toastBody: string,
  time: string,
  soundSetting: boolean,
  positionInQueue: number,
  queueName: string,
  handleDismiss: () => void,
  handleOpenQueue: () => void,
};

const LatNotification = ({
  toastBody,
  soundSetting,
  time,
  positionInQueue,
  queueName,
  handleDismiss,
  handleOpenQueue,
}: LATNotificationProps): React.Node => {
  const {t, makeKey, language} = useLabelTranslation();
  const customIcon = (
    <Icon name="bell" size="medium" color="danger" type="solid" />
  );
  const timeZone = useSelector(getTimezone);
  const formattedTime = moment
    .tz(moment.unix(time), timeZone)
    .locale(language)
    .format('L LT'); // Locale-specific date and time format
  const toastTitle = `${t(
    'Someone_joined_your_queue',
    'Someone joined your queue',
  )}!`;
  const toastFormattedBody =
    positionInQueue <= 1
      ? t(
          makeKey(
            'There_is_position_in_queue_person_waiting_in_the_queue_name',
          ),
          {
            position_in_queue: positionInQueue,
            queue_name: queueName,
            defaultValue: toastBody,
          },
        )
      : t(
          makeKey(
            'There_are_position_in_queue_people_waiting_in_the_queue_name',
          ),
          {
            position_in_queue: positionInQueue,
            queue_name: queueName,
            defaultValue: toastBody,
          },
        );
  return (
    <div className={css.wrapper}>
      <Toast
        initialFocus={0}
        semantic="danger"
        time={formattedTime}
        customIcon={customIcon}
        onClose={handleDismiss}
      >
        <ToastTitle>{toastTitle}</ToastTitle>
        <ToastBody>{toastFormattedBody}</ToastBody>
        <ToastFooter>
          <Button onClick={handleDismiss}>{`${t(
            'Dismiss',
            'Dismiss',
          )}`}</Button>
          <Button onClick={handleOpenQueue}>{`${t(
            'Open_Queue',
            'Open Queue',
          )}`}</Button>
        </ToastFooter>
      </Toast>
      <ChatSoundEffect
        // we don't need unique playId here, it should play sound upon mount
        playId={soundSetting}
        soundSetting={soundSetting}
        playOnMount={true}
        media={[
          {
            src: bellMp3,
            type: 'audio/mpeg',
          },
          {
            src: bellWav,
            type: 'audio/wav',
          },
        ]}
      />
    </div>
  );
};

export default LatNotification;
